import { useMemo } from "react";
import { useLocation } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { BreadcrumbItemType } from "@/components/Sidebar";
import { toDisplayName } from "@/features/schema-management/context/SchemaEditContext";
import { api } from "@/lib/api/client";

export const useBreadcrumbs = (): BreadcrumbItemType[] => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.searchStr);
    const appIdFromUrl = searchParams.get("appId");

    const sampleViewMatch = location.pathname.match(/\/agent\/document\/extract\/(\d+)\/(\d+)/);
    const documentTypeId = sampleViewMatch ? parseInt(sampleViewMatch[1]) : NaN;
    const sampleId = sampleViewMatch ? parseInt(sampleViewMatch[2]) : NaN;
    const isSampleView = !!sampleViewMatch;

    const { data: docTypeData } = useQuery({
        queryKey: ["document-type", documentTypeId],
        queryFn: () => api.getDocumentType(documentTypeId),
        enabled: isSampleView,
        staleTime: Infinity,
    });

    const { data: sampleData } = useQuery({
        queryKey: ["document-type-sample", documentTypeId, sampleId],
        queryFn: () => api.getDocumentTypeSample(documentTypeId, sampleId),
        enabled: isSampleView,
        staleTime: Infinity,
    });

    return useMemo(() => {
        const crumbs: BreadcrumbItemType[] = [];

        if (location.pathname.includes("/agent/application")) {
            crumbs.push({
                title: "Applications",
                href: appIdFromUrl ? "/agent/application" : undefined,
            });

            if (appIdFromUrl) {
                crumbs.push({
                    title: `Application #${appIdFromUrl}`,
                });
            }
        } else if (location.pathname.includes("/agent/policy")) {
            crumbs.push({
                title: "Policy Engine",
            });
        } else if (location.pathname.endsWith("/agent/document/settings")) {
            crumbs.push({
                title: "Settings",
            });
        } else if (location.pathname.endsWith("/agent/document/add") || location.pathname.endsWith("/agent/document/schema-edit")) {
            crumbs.push({
                title: "Documents",
                href: "/agent/document",
            });
            crumbs.push({
                title: "Add Document Type",
            });
        } else if (isSampleView) {
            const docTypeName = docTypeData?.documentType?.documentType
                ? toDisplayName(docTypeData.documentType.documentType)
                : "Document";
            const sampleName = sampleData?.sample?.fileName ?? "Sample";

            crumbs.push({ title: "Documents", href: "/agent/document/extract" });
            crumbs.push({ title: docTypeName, href: `/agent/document/extract/${documentTypeId}` });
            crumbs.push({ title: sampleName });
        } else if (location.pathname.match(/\/agent\/document\/extract\/.+/)) {
            const name = searchParams.get("name")
            crumbs.push({
                title: "Documents",
                href: "/agent/document/extract",
            });
            crumbs.push({
                title: name || "Document",
            });
        } else if (location.pathname.includes("/agent/document/extract")) {
            crumbs.push({
                title: "Extract Data",
            });
        } else if (location.pathname.includes("/agent/document")) {
            crumbs.push({
                title: "Documents",
            });
        } else if (location.pathname === "/") {
            crumbs.push({
                title: "Home",
            });
        }

        if (crumbs.length > 0) {
            crumbs[crumbs.length - 1].href = undefined;
        }

        return crumbs;
    }, [location.pathname, appIdFromUrl, isSampleView, documentTypeId, docTypeData, sampleData]);
};
